import { motion, AnimatePresence } from "framer-motion";
import { Edit2, Trash2, Eye, AlertTriangle, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../components/ui/context-menu";
import InlineEditCell from "./InlineEditCell";

const TABLE_FADE = { initial: { opacity: 0, y: 15 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.15, duration: 0.25 } };
const ROW_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };

const getRankClass = (rank) => {
  const classes = {
    "HEAD ADMIN": "rank-head-admin",
    "SENIOR ADMIN": "rank-senior-admin",
    "ADMIN": "rank-admin",
    "SENIOR MOD": "rank-senior-mod",
    "MOD": "rank-mod",
    "JRMOD": "rank-jrmod",
  };
  return classes[rank] || "rank-jrmod";
};

const getActivityClass = (activity) => {
  if (activity === "PASSED") return "status-passed";
  if (activity === "FAILED") return "status-failed";
  if (activity === "EXEMPT") return "status-exempt";
  return "status-none";
};

const StaffTable = ({
  staff,
  loading,
  fetchError,
  flaggedStaff,
  onEdit,
  onDelete,
  onViewProfile,
  onRetry,
  onStaffUpdated,
}) => {
  // Indicators for each staff member
  const getIndicators = (member) => {
    const indicators = [];
    if (member.suspended === "Suspended") {
      indicators.push({ label: "SUS", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" });
    }
    if (member.loa_status === "On LOA") {
      indicators.push({ label: "LOA", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" });
    }
    if (member.strike_status && member.strike_status !== "None") {
      indicators.push({ label: member.strike_status.replace("Strike ", "S"), cls: "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/30" });
    }
    if (member.warning_status && member.warning_status !== "None") {
      indicators.push({ label: "W", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" });
    }
    return indicators;
  };

  return (
    <motion.div
      {...TABLE_FADE}
      className="surface-card overflow-hidden"
      data-testid="staff-table-container"
    >
      {/* Mobile card view (< md) */}
      <div className="md:hidden divide-y divide-zinc-800" data-testid="staff-cards">
        <AnimatePresence>
          {staff.map((member, index) => {
            const indicators = getIndicators(member);
            const isFlagged = flaggedStaff.some(
              (f) => f.username.toLowerCase() === member.username?.toLowerCase()
            );
            return (
              <motion.div
                key={member.id}
                {...ROW_FADE}
                transition={{ delay: index * 0.02, duration: 0.15 }}
                onClick={() => onViewProfile(member)}
                className={`relative px-4 py-3 active:bg-zinc-900/50 transition-colors cursor-pointer ${
                  member.notes === "CONCERN" ? "concern-highlight" : ""
                }`}
                data-testid={`staff-card-${member.id}`}
              >
                {/* Row 1: name + actions trigger */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-zinc-100 truncate">
                        {member.name}
                      </span>
                      {isFlagged && (
                        <AlertTriangle
                          className="w-3.5 h-3.5 text-[#FF3366] shrink-0 glow-rose"
                          title="Flagged for review"
                        />
                      )}
                    </div>
                    <p className="font-mono text-[11px] text-zinc-500 truncate">
                      @{member.username}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 w-9 h-9 -mt-1 -mr-2 flex items-center justify-center active:bg-zinc-800 transition-colors"
                        data-testid={`actions-menu-${member.id}`}
                        aria-label="Actions"
                      >
                        <MoreVertical className="w-4 h-4 text-zinc-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="bg-[#121212] border-zinc-800 rounded-none min-w-[140px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenuItem
                        onClick={() => onViewProfile(member)}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                        data-testid={`view-profile-${member.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-2 text-[#00E5FF]" />
                        VIEW PROFILE
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onEdit(member)}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                        data-testid={`edit-staff-${member.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-2 text-blue-400" />
                        EDIT
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem
                        onClick={() => onDelete(member)}
                        className="text-[#FF3366] hover:text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none font-mono text-xs cursor-pointer"
                        data-testid={`delete-staff-${member.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                        DELETE
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Row 2: rank + activity + indicators */}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`rank-badge ${getRankClass(member.rank)}`}>
                    {member.rank}
                  </span>
                  <span className={`status-badge ${getActivityClass(member.general_activity)}`}>
                    {member.general_activity || "—"}
                  </span>
                  {indicators.map((ind) => (
                    <span
                      key={ind.label}
                      className={`text-[9px] font-mono font-bold px-1.5 py-0.5 border ${ind.cls}`}
                    >
                      {ind.label}
                    </span>
                  ))}
                </div>

                {/* Row 3: time + reports */}
                <div className="flex items-center gap-4 mt-2 font-mono text-[11px]">
                  <div className="flex items-center gap-1.5">
                    <span className="text-zinc-600 uppercase tracking-wider">Time</span>
                    <span className="text-zinc-300">{member.total_time || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-zinc-600 uppercase tracking-wider">Reports</span>
                    <span className="text-purple-400">{member.game_reports || 0}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {staff.length === 0 && !loading && (
          <div className="text-center py-16 px-4">
            {fetchError ? (
              <>
                <p className="text-[#FF3366] font-mono text-sm mb-2">LOAD FAILED</p>
                <p className="text-zinc-600 text-xs mb-4 font-mono">{fetchError}</p>
                <button onClick={onRetry} className="btn-primary" data-testid="retry-fetch-btn-mobile">
                  RETRY
                </button>
              </>
            ) : (
              <p className="text-zinc-600 font-mono text-xs">NO STAFF MEMBERS FOUND</p>
            )}
          </div>
        )}

        {loading && (
          <div className="text-center py-16">
            <div className="w-5 h-5 border-2 border-[#00E5FF]/30 border-t-[#00E5FF] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-zinc-600 font-mono text-xs">LOADING STAFF DATA...</p>
          </div>
        )}
      </div>

      {/* Desktop table view (md+) */}
      <div className="hidden md:block overflow-x-auto scrollbar-thin">
        <table className="w-full" data-testid="staff-table">
          <thead>
            <tr className="table-header">
              <th className="text-left py-3 px-4">Name</th>
              <th className="text-left py-3 px-4">Rank</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Time</th>
              <th className="text-left py-3 px-4">Reports</th>
              <th className="text-right py-3 px-4 w-16">Actions</th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
              {staff.map((member, index) => {
                const indicators = getIndicators(member);
                const isFlagged = flaggedStaff.some(
                  (f) => f.username.toLowerCase() === member.username?.toLowerCase()
                );
                return (
                  <ContextMenu key={member.id}>
                    <ContextMenuTrigger asChild>
                      <motion.tr
                        {...ROW_FADE}
                        transition={{ delay: index * 0.02, duration: 0.15 }}
                        className={`table-row cursor-pointer ${
                          member.notes === "CONCERN" ? "concern-highlight" : ""
                        }`}
                        data-testid={`staff-row-${member.id}`}
                        onClick={() => onViewProfile(member)}
                      >
                    {/* Name + Username + Indicators */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-zinc-200 truncate">
                              {member.name}
                            </span>
                            {isFlagged && (
                              <AlertTriangle
                                className="w-3 h-3 text-[#FF3366] shrink-0 glow-rose"
                                title="Flagged for review"
                              />
                            )}
                          </div>
                          <p className="font-mono text-[11px] text-zinc-600 truncate">
                            @{member.username}
                          </p>
                        </div>
                        {/* Status indicator chips */}
                        {indicators.length > 0 && (
                          <div className="flex gap-1 shrink-0">
                            {indicators.map((ind) => (
                              <span
                                key={ind.label}
                                className={`text-[8px] font-mono font-bold px-1 py-0.5 border ${ind.cls}`}
                              >
                                {ind.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Rank */}
                    <td className="py-3 px-4">
                      <span className={`rank-badge ${getRankClass(member.rank)}`}>
                        {member.rank}
                      </span>
                    </td>

                    {/* Activity Status */}
                    <td className="py-3 px-4">
                      <span className={`status-badge ${getActivityClass(member.general_activity)}`}>
                        {member.general_activity || "-"}
                      </span>
                    </td>

                    {/* Time — double-click to edit */}
                    <td className="py-3 px-4">
                      <InlineEditCell
                        staff={member}
                        field="total_time"
                        onSaved={onStaffUpdated}
                        placeholder="e.g. 2h 30m"
                        className="font-mono text-xs text-zinc-400"
                        render={(v) => (
                          <span className="font-mono text-xs text-zinc-400">{v || "—"}</span>
                        )}
                      />
                    </td>

                    {/* Game Reports — double-click to edit */}
                    <td className="py-3 px-4">
                      <InlineEditCell
                        staff={member}
                        field="game_reports"
                        onSaved={onStaffUpdated}
                        placeholder="0"
                        render={(v) => (
                          <span className="font-mono text-xs text-purple-400">{v ?? 0}</span>
                        )}
                      />
                    </td>

                    {/* Actions Dropdown */}
                    <td className="py-3 px-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 hover:bg-zinc-800 transition-colors inline-flex items-center justify-center"
                            data-testid={`actions-menu-${member.id}`}
                          >
                            <MoreVertical className="w-4 h-4 text-zinc-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-[#121212] border-zinc-800 rounded-none min-w-[140px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            onClick={() => onViewProfile(member)}
                            className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                            data-testid={`view-profile-${member.id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-2 text-[#00E5FF]" />
                            VIEW PROFILE
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onEdit(member)}
                            className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                            data-testid={`edit-staff-${member.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-2 text-blue-400" />
                            EDIT
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-zinc-800" />
                          <DropdownMenuItem
                            onClick={() => onDelete(member)}
                            className="text-[#FF3366] hover:text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none font-mono text-xs cursor-pointer"
                            data-testid={`delete-staff-${member.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" />
                            DELETE
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </motion.tr>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="bg-[#121212] border-zinc-800 rounded-none min-w-[160px]">
                      <ContextMenuItem
                        onClick={() => onViewProfile(member)}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                        data-testid={`ctx-view-profile-${member.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-2 text-[#00E5FF]" />
                        VIEW PROFILE
                      </ContextMenuItem>
                      <ContextMenuItem
                        onClick={() => onEdit(member)}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-none font-mono text-xs cursor-pointer"
                        data-testid={`ctx-edit-staff-${member.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-2 text-blue-400" />
                        EDIT
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })}
            </AnimatePresence>
          </tbody>
        </table>

        {staff.length === 0 && !loading && (
          <div className="text-center py-16">
            {fetchError ? (
              <>
                <p className="text-[#FF3366] font-mono text-sm mb-2">LOAD FAILED</p>
                <p className="text-zinc-600 text-xs mb-4 font-mono">{fetchError}</p>
                <button onClick={onRetry} className="btn-primary" data-testid="retry-fetch-btn">
                  RETRY
                </button>
              </>
            ) : (
              <p className="text-zinc-600 font-mono text-xs">NO STAFF MEMBERS FOUND</p>
            )}
          </div>
        )}

        {loading && (
          <div className="text-center py-16">
            <div className="w-5 h-5 border-2 border-[#00E5FF]/30 border-t-[#00E5FF] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-zinc-600 font-mono text-xs">LOADING STAFF DATA...</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default StaffTable;
